import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { SearchableSelect } from '../../components/ui/SearchableSelect.jsx';
import { showToast } from '../../components/ui/Toaster.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { inventoryApi } from '../../services/inventoryApi.js';
import { salesApi } from '../../services/salesApi.js';
import { addDaysIso, formatCurrency, todayIso } from '../../utils/formatters.js';
import { chfEquivalent } from '../../utils/fx.js';
import { computeInvoiceTotals } from '../../utils/vat.js';
import { CustomerCreateModal } from '../sales/CustomerCreateModal.jsx';

// Swiss VAT rates (2024+): normal / reduced / lodging / exempt.
const TAX_RATES = ['8.1', '2.6', '3.8', '0'];

function emptyLine() {
  return { accountId: '', description: '', quantity: '1', unitPrice: '0', taxRate: '8.1' };
}

function emptyForm() {
  return {
    type: 'SALES',
    counterpartyId: '',
    poId: '',
    invoiceDate: todayIso(),
    dueDate: addDaysIso(30),
    currency: 'CHF',
    exchangeRate: '1',
    reverseCharge: false,
    notes: '',
    lines: [emptyLine()],
  };
}

export function InvoiceCreateModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState(null);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [selectedCounterparty, setSelectedCounterparty] = useState(null);

  const accounts = useQuery({
    queryKey: ['finance', 'accounts'],
    queryFn: () => financeApi.listAccounts().then((r) => r.items || []),
    enabled: open,
  });
  const eligibleAccounts = useMemo(() => {
    const list = accounts.data || [];
    if (form.type === 'SALES') {
      return list.filter((a) => a.type === 'REVENUE' && a.isActive);
    }
    return list.filter((a) => (a.type === 'EXPENSE' || a.type === 'ASSET') && a.isActive);
  }, [accounts.data, form.type]);

  const totals = computeInvoiceTotals(form.lines);

  const currencies = useQuery({
    queryKey: ['finance', 'currencies', 'active'],
    queryFn: () => financeApi.listCurrencies({ active: true }).then((r) => r.items || []),
    enabled: open,
  });
  const currencyOptions = currencies.data || [];

  const latestRate = useQuery({
    queryKey: ['finance', 'exchange-rate-latest', form.currency],
    queryFn: () =>
      financeApi
        .listExchangeRates({ currency: form.currency, limit: 1 })
        .then((r) => (r.items || [])[0] || null),
    enabled: open && form.currency !== 'CHF',
  });

  // Prefill the rate field from the latest stored rate. Switching the currency
  // resets exchangeRate to '' (see the select's onChange), and we only fill
  // while it is still '' - manual input is never overwritten by re-renders.
  useEffect(() => {
    const fetched = latestRate.data?.rate;
    if (!fetched) {
      return;
    }
    setForm((p) => (p.currency !== 'CHF' && p.exchangeRate === ''
      ? { ...p, exchangeRate: String(Number(fetched)) }
      : p));
    // form.currency re-fires the prefill when the user switches back to a
    // currency whose rate is already cached - structural sharing keeps
    // latestRate.data identical, so it alone would not re-trigger.
  }, [latestRate.data, form.currency]);

  const hasNoStoredRate = form.currency !== 'CHF' && latestRate.isSuccess && !latestRate.data;

  // Purchase orders eligible for the 3-way match link: fully received, and
  // (when a vendor is picked) belonging to that vendor.
  const purchaseOrders = useQuery({
    queryKey: ['inventory', 'purchase-orders'],
    queryFn: () => inventoryApi.listPurchaseOrders().then((r) => r.items || []),
    enabled: open && form.type === 'PURCHASE',
  });
  const receivedPos = useMemo(() => {
    return (purchaseOrders.data || []).filter(
      (po) => po.status === 'RECEIVED'
        && (!form.counterpartyId || po.supplierId === form.counterpartyId),
    );
  }, [purchaseOrders.data, form.counterpartyId]);
  const selectedPo = receivedPos.find((po) => po.id === form.poId) || null;

  const create = useMutation({
    mutationFn: (body) => financeApi.createInvoice(body),
    onSuccess: (created) => {
      // The backend dedupes on (sourceType, sourceId) and returns the EXISTING
      // invoice (201) flagged with `deduped: true` when the PO is already billed.
      if (created?.deduped === true && created?.invoiceNumber) {
        showToast(t('match.alreadyBilled', { number: created.invoiceNumber }), 'success');
      }
      queryClient.invalidateQueries({ queryKey: ['finance', 'invoices'] });
      setForm(emptyForm());
      setSelectedCounterparty(null);
      setError(null);
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function patchField(key, value) {
    setForm((p) => ({ ...p, [key]: value }));
  }

  function patchLine(idx, key, value) {
    setForm((p) => ({
      ...p,
      lines: p.lines.map((line, i) => (i === idx ? { ...line, [key]: value } : line)),
    }));
  }

  function addLine() {
    setForm((p) => ({ ...p, lines: [...p.lines, emptyLine()] }));
  }

  function removeLine(idx) {
    setForm((p) => ({
      ...p,
      lines: p.lines.length > 1 ? p.lines.filter((_, i) => i !== idx) : p.lines,
    }));
  }

  function submit(e) {
    e.preventDefault();
    setError(null);
    if (!form.counterpartyId) {
      setError(t('finance.invoices.modal.pickCounterparty'));
      return;
    }
    // The Create button lives in the Modal footer outside the <form>, so the
    // rate input's `required` never fires - guard the FX rate here instead.
    if (form.currency !== 'CHF' && !(Number(form.exchangeRate) > 0)) {
      setError(t('invoice.fx.noRate', { currency: form.currency }));
      return;
    }
    const payload = {
      type: form.type,
      invoiceDate: form.invoiceDate,
      dueDate: form.dueDate,
      currency: form.currency,
      notes: form.notes || undefined,
      lines: form.lines.map((l) => ({
        accountId: l.accountId,
        description: l.description.trim() || ' ',
        quantity: Number(l.quantity || 0),
        unitPrice: Number(l.unitPrice || 0),
        taxRate: Number(l.taxRate),
      })),
    };
    if (form.currency !== 'CHF') {
      payload.exchangeRate = Number(form.exchangeRate);
    }
    if (form.type === 'SALES') {
      payload.customerId = form.counterpartyId;
    } else {
      payload.vendorId = form.counterpartyId;
      if (form.reverseCharge) {
        payload.reverseCharge = true;
      }
      if (selectedPo) {
        payload.sourceType = 'PURCHASE_ORDER';
        payload.sourceId = selectedPo.id;
      }
    }
    create.mutate(payload);
  }

  async function loadCounterparties(q) {
    if (form.type === 'SALES') {
      const { items } = await salesApi.listCustomers({ q: q || undefined, limit: 20 });
      return (items || []).map((c) => ({
        id: c.id,
        label: `${c.code} - ${c.name}`,
        sublabel: c.email || undefined,
      }));
    }
    const needle = q.toLowerCase();
    const { items } = await inventoryApi.listSuppliers();
    return (items || [])
      .filter(
        (s) =>
          !needle ||
          s.code?.toLowerCase().includes(needle) ||
          s.name?.toLowerCase().includes(needle) ||
          s.email?.toLowerCase().includes(needle),
      )
      .slice(0, 20)
      .map((s) => ({ id: s.id, label: `${s.code} - ${s.name}`, sublabel: s.email || undefined }));
  }

  return (
    <>
    <Modal
      open={open}
      onClose={onClose}
      title={t('finance.invoices.modal.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={submit} disabled={create.isPending}>
            {create.isPending ? `${t('common.save')}…` : t('common.create')}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('col.type')}</span>
            <select
              className="select"
              value={form.type}
              onChange={(e) => {
                patchField('type', e.target.value);
                patchField('counterpartyId', '');
                patchField('poId', '');
                setSelectedCounterparty(null);
                setForm((p) => ({ ...p, lines: [emptyLine()] }));
              }}
            >
              <option value="SALES">{t('invoiceType.SALES')}</option>
              <option value="PURCHASE">{t('invoiceType.PURCHASE')}</option>
            </select>
          </label>
          <label className="field">
            <span className="field-label">
              {form.type === 'SALES' ? t('finance.invoices.modal.customer') : t('finance.invoices.modal.vendor')}
            </span>
            <div className="inline-create">
              <SearchableSelect
                value={form.counterpartyId}
                selectedOption={selectedCounterparty}
                onChange={(id, option) => {
                  patchField('counterpartyId', id);
                  setSelectedCounterparty(option);
                }}
                loadOptions={loadCounterparties}
                placeholder={t('finance.invoices.modal.searchCounterparty')}
                emptyText={t('finance.invoices.modal.noCounterparty')}
                loadingText={`${t('common.search')}…`}
              />
              {form.type === 'SALES' && (
                <Button
                  type="button"
                  variant="ghost"
                  className="inline-create-btn"
                  onClick={() => setCustomerModalOpen(true)}
                  title={t('sales.customers.action.new')}
                >
                  + {t('common.new')}
                </Button>
              )}
            </div>
          </label>
        </div>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('finance.invoices.modal.invoiceDate')}</span>
            <input
              className="input"
              type="date"
              value={form.invoiceDate}
              onChange={(e) => patchField('invoiceDate', e.target.value)}
              required
            />
          </label>
          <label className="field">
            <span className="field-label">{t('finance.invoices.modal.dueDate')}</span>
            <input
              className="input"
              type="date"
              value={form.dueDate}
              onChange={(e) => patchField('dueDate', e.target.value)}
              required
            />
          </label>
        </div>

        {form.type === 'PURCHASE' && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <input type="checkbox" checked={form.reverseCharge} onChange={(e) => patchField('reverseCharge', e.target.checked)} />
            <span className="field-label" style={{ margin: 0 }}>{t('invoice.reverseCharge')}</span>
          </label>
        )}

        <div className="field-row">
          <label className="field" style={{ flex: '0 0 120px' }}>
            <span className="field-label">{t('invoice.fx.currency')}</span>
            <select
              className="select"
              value={form.currency}
              onChange={(e) => {
                const code = e.target.value;
                // FX starts blank so the latest stored rate can prefill; CHF pins 1.
                setForm((p) => ({ ...p, currency: code, exchangeRate: code === 'CHF' ? '1' : '' }));
              }}
            >
              {(currencyOptions.length > 0 ? currencyOptions : [{ code: 'CHF' }]).map((c) => (
                <option key={c.code} value={c.code}>{c.code}</option>
              ))}
            </select>
          </label>
          {form.currency !== 'CHF' && (
            <label className="field">
              <span className="field-label">{t('invoice.fx.rate')}</span>
              <input
                className="input"
                type="number"
                min="0.000001"
                step="0.000001"
                value={form.exchangeRate}
                onChange={(e) => patchField('exchangeRate', e.target.value)}
                required
              />
            </label>
          )}
        </div>
        {hasNoStoredRate && (
          <div className="alert warning" role="note">
            {t('invoice.fx.noRate', { currency: form.currency })}
          </div>
        )}

        {form.type === 'PURCHASE' && (
          <label className="field">
            <span className="field-label">{t('match.poSelect')}</span>
            <select
              className="select"
              value={form.poId}
              onChange={(e) => patchField('poId', e.target.value)}
            >
              <option value="">{t('match.poNone')}</option>
              {receivedPos.map((po) => (
                <option key={po.id} value={po.id}>
                  {po.poNumber} - {formatCurrency(po.totalAmount)}
                </option>
              ))}
            </select>
          </label>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span className="field-label">{t('finance.invoices.modal.lines')}</span>
          <Button size="sm" variant="ghost" onClick={addLine}>+ {t('finance.invoices.modal.addLine')}</Button>
        </div>
        {form.lines.map((line, idx) => (
          <div
            key={idx}
            style={{
              display: 'flex', flexDirection: 'column', gap: 'var(--space-2)',
              padding: 'var(--space-3)', border: '1px solid var(--color-hairline)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <label className="field">
              <span className="field-label">{t('finance.invoices.modal.account')}</span>
              <select
                className="select"
                value={line.accountId}
                onChange={(e) => patchLine(idx, 'accountId', e.target.value)}
                required
              >
                <option value="">{t('finance.invoices.modal.pickCounterparty')}</option>
                {eligibleAccounts.map((a) => (
                  <option key={a.id} value={a.id}>{a.code} - {a.name}</option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="field-label">{t('finance.invoices.modal.lineDescription')}</span>
              <input
                className="input"
                value={line.description}
                onChange={(e) => patchLine(idx, 'description', e.target.value)}
                required
              />
            </label>
            <div className="field-row">
              <label className="field">
                <span className="field-label">{t('finance.invoices.modal.quantity')}</span>
                <input
                  className="input"
                  type="number"
                  min="0.0001"
                  step="any"
                  value={line.quantity}
                  onChange={(e) => patchLine(idx, 'quantity', e.target.value)}
                  required
                />
              </label>
              <label className="field">
                <span className="field-label">{t('finance.invoices.modal.unitPrice')}</span>
                <input
                  className="input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={line.unitPrice}
                  onChange={(e) => patchLine(idx, 'unitPrice', e.target.value)}
                  required
                />
              </label>
              <label className="field" style={{ flex: '0 0 90px' }}>
                <span className="field-label">{t('vat.rateCol')}</span>
                <select
                  className="select"
                  value={line.taxRate}
                  onChange={(e) => patchLine(idx, 'taxRate', e.target.value)}
                >
                  {TAX_RATES.map((rate) => (
                    <option key={rate} value={rate}>{rate} %</option>
                  ))}
                </select>
              </label>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="muted text-sm">
                {t('finance.invoices.modal.lineTotal')}: <strong>{formatCurrency(totals.lineNets[idx], form.currency)}</strong>
              </span>
              {form.lines.length > 1 && (
                <Button size="sm" variant="danger" onClick={() => removeLine(idx)}>
                  {t('finance.invoices.modal.removeLine')}
                </Button>
              )}
            </div>
          </div>
        ))}

        <div style={{
          display: 'flex', flexDirection: 'column', gap: 'var(--space-1)',
          borderTop: '1px solid var(--color-hairline)', paddingTop: 'var(--space-3)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }} className="muted text-sm">
            <span>{t('vat.net')}</span>
            <span className="mono">{formatCurrency(totals.net, form.currency)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }} className="muted text-sm">
            <span>{t('vat.tax')}</span>
            <span className="mono">{formatCurrency(totals.tax, form.currency)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>{t('finance.invoices.modal.invoiceTotal')}</span>
            <strong className="mono">{formatCurrency(totals.gross, form.currency)}</strong>
          </div>
          {form.currency !== 'CHF' && Number(form.exchangeRate) > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }} className="muted text-sm">
              <span>≈ {t('invoice.fx.chfEquivalent')}</span>
              <span className="mono">{formatCurrency(chfEquivalent(totals.gross.toFixed(2), form.exchangeRate), 'CHF')}</span>
            </div>
          )}
        </div>

        <label className="field">
          <span className="field-label">{t('finance.invoices.modal.notes')}</span>
          <textarea
            className="textarea"
            rows={2}
            value={form.notes}
            onChange={(e) => patchField('notes', e.target.value)}
          />
        </label>

        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
    <CustomerCreateModal
      open={customerModalOpen}
      onClose={() => setCustomerModalOpen(false)}
      onCreated={(created) => {
        queryClient.invalidateQueries({ queryKey: ['sales', 'customers'] });
        if (created?.id) {
          patchField('counterpartyId', created.id);
          setSelectedCounterparty({ id: created.id, label: `${created.code} - ${created.name}` });
        }
      }}
    />
    </>
  );
}
